import type { CapitalGlobalProposalStepProps } from '@/features/proposals/components/capital-global/capital-global-proposal-step.types';
import {
  canRetryDocusignSignature,
  canStartDigitalSignature,
  canSyncDocusignSignature,
  getDigitalSignatureBlockedReason,
  getPhysicalSignatureBlockedReason,
  isPhysicalSignatureButtonBlocked,
  isPhysicalSignatureMethodLocked,
  resolveCapitalGlobalLockedSignatureMethod,
} from '@/features/proposals/components/capital-global/utils/capital-global-docusign-signature.util';
import { isCapitalGlobalPhysicalSignatureUploadPending } from '@/features/proposals/components/capital-global/utils/capital-global-proposal-editability.util';
import {
  getCapitalGlobalProposalAttachmentDisplayFileName,
  hasCapitalGlobalSignedProposalAttachment,
} from '@/features/proposals/components/capital-global/utils/capital-global-proposal-attachment.util';
import { mapProposalToResumePremium } from '@/features/proposals/components/capital-global/utils/map-proposal-to-resume-premium.util';
import { useCapitalGlobalProposalInstallmentTypes } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-installment-types.hook';
import { useCapitalGlobalProposalPaymentTypes } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';
import {
  useCapitalGlobalDocusignSignatureStatus,
  useCreateCapitalGlobalDocusignSignature,
  useRetryCapitalGlobalDocusignSignature,
  useSyncCapitalGlobalDocusignSignature,
  capitalGlobalProposalQueryKey,
} from '@/features/proposals/hooks/capital-global/use-capital-global-docusign-signature.hook';
import {
  useCapitalGlobalProposalAttachments,
  mapAttachmentsByDocumentType,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-attachments.hook';
import {
  useCapitalGlobalProposalSignatureMethod,
  useChooseCapitalGlobalProposalPhysicalSignatureMethod,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-signature-method.hook';
import { useProposalsGeneratePdf } from '@/features/proposals/hooks/use-proposals-generate-pdf.hook';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { getErrorStatusCode } from '@/shared/utils';
import { getProposalCategory } from '@/features/proposals/utils/proposal-category';
import { mapProposalDetailsToCalculation } from '@/features/quotes/components/capital-global/steps/utils/map-proposal-details-to-calculation.util';
import { resolveStipulantSegmentCode } from '@/features/quotes/components/capital-global/steps/utils/resolve-stipulant-segment-code.util';
import type { CalculationViewData } from '@/features/quotes/components/capital-global/steps/step-calculation.types';
import { useCapitalGlobalCoveragesCatalog } from '@/features/quotes/hooks/capital-global/use-capital-global-coverages-catalog.hook';
import { useCapitalGlobalBusinessSegmentLabel } from '@/features/quotes/hooks/capital-global/use-capital-global-business-segment-label.hook';
import { capitalGlobalProposalsService } from '@/services/proposals/capital-global';
import type { ProposalDetails } from '@/services/quotes/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { toaster } from '@/shared/ui/toaster.component';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

type PendingSignatureMethod = 'physical' | 'digital';

const EMPTY_CALCULATION: CalculationViewData = {
  installments: [],
  insuranceDataCard: { title: 'Dados do Seguro', items: [] },
  stipulantDataCard: { title: 'Dados do Estipulante', items: [] },
  calculationDataCard: { title: 'Dados do Cálculo', items: [] },
  calculationDataByStipulant: [],
  stipulantAccordions: [],
  commercialization: { brokeragePercent: '0,00 %', agencingPercent: '0,00 %' },
};

type Args = Pick<
  CapitalGlobalProposalStepProps,
  'proposal' | 'policyId' | 'isViewMode' | 'onNext' | 'canGoNext'
>;

export function useCapitalGlobalProposalStep3ViewModel({
  proposal,
  policyId,
  isViewMode,
  onNext,
  canGoNext,
}: Args) {
  const queryClient = useQueryClient();
  const trimmedPolicyId = policyId.trim();
  const generatePdfMutation = useProposalsGeneratePdf(trimmedPolicyId);
  const signatureQuery =
    useCapitalGlobalDocusignSignatureStatus(trimmedPolicyId);
  const signatureMethodQuery =
    useCapitalGlobalProposalSignatureMethod(trimmedPolicyId);
  const choosePhysicalSignatureMethodMutation =
    useChooseCapitalGlobalProposalPhysicalSignatureMethod(trimmedPolicyId);
  const attachmentsQuery = useCapitalGlobalProposalAttachments(trimmedPolicyId);
  const createSignatureMutation =
    useCreateCapitalGlobalDocusignSignature(trimmedPolicyId);
  const syncSignatureMutation =
    useSyncCapitalGlobalDocusignSignature(trimmedPolicyId);
  const retrySignatureMutation =
    useRetryCapitalGlobalDocusignSignature(trimmedPolicyId);
  // Corretora inativa: nenhuma ação de assinatura (a Api barra com 403).
  const brokerGuard = useSelectedBrokerWriteGuard('proposal', 'other');

  const signature = signatureQuery.data ?? null;
  const signatureMethod = signatureMethodQuery.data ?? null;
  const isTransmitted =
    Number(proposal.statusPortal) === PortalStatus.ProposalTransmitted;
  const isInSignature =
    Number(proposal.statusPortal) === PortalStatus.ProposalInSignature;
  const canDownloadSignedPdf =
    signature?.status === 'completed' || isTransmitted;
  const canGeneratePdfInViewMode = canDownloadSignedPdf || isInSignature;
  const shouldHideSignatureActions = canDownloadSignedPdf;
  // Gerar o PDF da proposta é barrado pela Api com a corretora inativa; baixar
  // o PDF assinado (anexo já registrado) não.
  const isGeneratePdfBlocked = brokerGuard.blocked && !canDownloadSignedPdf;
  const lockedSignatureMethod = useMemo(
    () =>
      resolveCapitalGlobalLockedSignatureMethod({
        signatureMethod,
        signature,
      }),
    [signature, signatureMethod]
  );

  const attachmentsByType = useMemo(
    () => mapAttachmentsByDocumentType(attachmentsQuery.data ?? []),
    [attachmentsQuery.data]
  );

  const hasSignedProposal = useMemo(
    () => hasCapitalGlobalSignedProposalAttachment(attachmentsByType),
    [attachmentsByType]
  );

  const [pendingSignatureMethod, setPendingSignatureMethod] =
    useState<PendingSignatureMethod | null>(null);
  const [isDownloadingSignedPdf, setIsDownloadingSignedPdf] = useState(false);
  const previousSignatureStatusRef = useRef<string | undefined>(undefined);
  const orphanStatusRetryAttemptedRef = useRef(false);
  const choosePhysicalMutateAsync =
    choosePhysicalSignatureMethodMutation.mutateAsync;

  useEffect(() => {
    const status = signature?.status;
    const previousStatus = previousSignatureStatusRef.current;
    previousSignatureStatusRef.current = status;

    if (
      status === 'completed' &&
      previousStatus !== undefined &&
      previousStatus !== 'completed'
    ) {
      void queryClient.invalidateQueries({
        queryKey: capitalGlobalProposalQueryKey(trimmedPolicyId),
      });
      toaster.create({
        type: 'success',
        title: 'Proposta transmitida',
        description:
          'A assinatura foi concluída e a proposta foi marcada como transmitida.',
        duration: 5000,
      });
    }
  }, [queryClient, signature?.status, trimmedPolicyId]);

  useEffect(() => {
    if (orphanStatusRetryAttemptedRef.current) return;
    if (!trimmedPolicyId) return;
    if (signatureMethodQuery.isLoading) return;
    if (!isPhysicalSignatureMethodLocked(lockedSignatureMethod)) return;
    if (Number(proposal.statusPortal) !== PortalStatus.ProposalInProgress) {
      return;
    }

    orphanStatusRetryAttemptedRef.current = true;

    void choosePhysicalMutateAsync()
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: capitalGlobalProposalQueryKey(trimmedPolicyId),
        })
      )
      .catch(() => {
        // Retry silencioso: falha permanece visível no status até nova tentativa.
      });
  }, [
    choosePhysicalMutateAsync,
    lockedSignatureMethod,
    proposal.statusPortal,
    queryClient,
    signatureMethodQuery.isLoading,
    trimmedPolicyId,
  ]);

  const {
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    isLoading: isLoadingCatalog,
  } = useCapitalGlobalCoveragesCatalog();

  const proposalDetails = proposal as unknown as ProposalDetails;
  const segmentCode = resolveStipulantSegmentCode(proposalDetails);
  const { label: segmentLabel, isLoading: isLoadingSegmentLabel } =
    useCapitalGlobalBusinessSegmentLabel(segmentCode);

  const schemaCategory = getProposalCategory(proposal.productCode);
  const { paymentTypes } = useCapitalGlobalProposalPaymentTypes();
  const { premiumPeriodicityOptions: premiumPeriodicityCatalog } =
    useCapitalGlobalProposalInstallmentTypes();

  const premiumData = useMemo(
    () =>
      mapProposalToResumePremium(
        proposal,
        schemaCategory,
        paymentTypes,
        premiumPeriodicityCatalog
      ),
    [proposal, schemaCategory, paymentTypes, premiumPeriodicityCatalog]
  );

  const calculationData = useMemo(() => {
    if (!proposalDetails.policyId) return EMPTY_CALCULATION;

    return mapProposalDetailsToCalculation(proposalDetails, {
      coverageCatalog,
      assistanceCatalog,
      assistanceFuneralOptions,
      capitalizationOptions,
      segmentLabel,
    });
  }, [
    proposalDetails,
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    segmentLabel,
  ]);

  const isLoading =
    isLoadingCatalog ||
    isLoadingSegmentLabel ||
    signatureQuery.isLoading ||
    signatureMethodQuery.isLoading ||
    attachmentsQuery.isLoading;

  const isSignatureActionPending =
    createSignatureMutation.isPending ||
    syncSignatureMutation.isPending ||
    retrySignatureMutation.isPending;

  const isPhysicalSignatureBlocked =
    brokerGuard.blocked ||
    isPhysicalSignatureButtonBlocked({
      lockedMethod: lockedSignatureMethod,
      signature,
      hasSignedProposal,
    });
  const canStartDigital =
    !brokerGuard.blocked &&
    canStartDigitalSignature(signature, lockedSignatureMethod);
  // Sync/retry seguem visíveis (dependem do envelope); a trava da corretora
  // só desabilita, via `signatureActionsBlockedReason`.
  const canSync = canSyncDocusignSignature(signature);
  const canRetry = canRetryDocusignSignature(signature);
  const physicalSignatureBlockedReason =
    brokerGuard.reason ??
    getPhysicalSignatureBlockedReason(
      lockedSignatureMethod,
      signature,
      hasSignedProposal
    );
  const digitalSignatureBlockedReason =
    brokerGuard.reason ??
    getDigitalSignatureBlockedReason(lockedSignatureMethod);
  const canGoToUploadStep =
    isCapitalGlobalPhysicalSignatureUploadPending({
      signature,
      signatureMethod,
      hasSignedProposal,
    }) && canGoNext;

  const downloadSignedProposalPdf = useCallback(async () => {
    setIsDownloadingSignedPdf(true);

    try {
      const response = await capitalGlobalProposalsService.listAttachments(
        trimmedPolicyId,
        'signed_proposal'
      );

      if (!response.success) {
        throw new Error(response.error || 'Erro ao carregar PDF assinado');
      }

      const attachment = response.data?.items?.[0];
      if (!attachment?.downloadUrl) {
        toaster.create({
          type: 'error',
          title: 'PDF assinado indisponível',
          description: 'Verifique a assinatura e tente sincronizar.',
        });
        return;
      }

      const anchor = document.createElement('a');
      anchor.href = attachment.downloadUrl;
      anchor.download = getCapitalGlobalProposalAttachmentDisplayFileName(
        attachment.fileName,
        attachment.keyPath
      );
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao baixar PDF assinado',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    } finally {
      setIsDownloadingSignedPdf(false);
    }
  }, [trimmedPolicyId]);

  const handleGeneratePdf = useCallback(async () => {
    if (isViewMode && !canGeneratePdfInViewMode) return;
    if (isGeneratePdfBlocked) return;

    if (canDownloadSignedPdf) {
      await downloadSignedProposalPdf();
      return;
    }

    const productCode = proposal.productCode?.trim();
    if (!productCode) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar o PDF',
        description: 'Código do produto não encontrado.',
      });
      return;
    }

    try {
      await generatePdfMutation.mutateAsync({
        productCode,
        stage: 'proposal',
      });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao gerar PDF',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    canDownloadSignedPdf,
    canGeneratePdfInViewMode,
    downloadSignedProposalPdf,
    generatePdfMutation,
    isGeneratePdfBlocked,
    isViewMode,
    proposal.productCode,
  ]);

  const handlePhysicalSignature = useCallback(async () => {
    if (isViewMode || isPhysicalSignatureBlocked) return;

    const productCode = proposal.productCode?.trim();
    if (!productCode) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível gerar o PDF',
        description: 'Código do produto não encontrado.',
      });
      return;
    }

    try {
      await choosePhysicalSignatureMethodMutation.mutateAsync();
      await generatePdfMutation.mutateAsync({
        productCode,
        stage: 'proposal',
      });
      await queryClient.invalidateQueries({
        queryKey: capitalGlobalProposalQueryKey(trimmedPolicyId),
      });
      onNext();
    } catch (error) {
      const statusCode = getErrorStatusCode(error);

      if (statusCode === 409) {
        await signatureMethodQuery.refetch();
      }

      toaster.create({
        type: 'error',
        title: 'Erro ao confirmar assinatura física',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    choosePhysicalSignatureMethodMutation,
    generatePdfMutation,
    isPhysicalSignatureBlocked,
    isViewMode,
    onNext,
    proposal.productCode,
    queryClient,
    signatureMethodQuery,
    trimmedPolicyId,
  ]);

  const handleDigitalSignature = useCallback(async () => {
    if (isViewMode || !canStartDigital || isSignatureActionPending) return;

    try {
      await createSignatureMutation.mutateAsync();
      await signatureMethodQuery.refetch();
      toaster.create({
        type: 'success',
        title: 'Assinatura digital iniciada',
        description:
          'Convites enviados por e-mail ao estipulante e ao corretor.',
      });
    } catch (error) {
      const statusCode = getErrorStatusCode(error);

      if (statusCode === 409) {
        await signatureQuery.refetch();
        await signatureMethodQuery.refetch();
        toaster.create({
          type: 'info',
          title: 'Assinatura já em andamento',
          description:
            error instanceof Error
              ? error.message
              : 'Já existe uma assinatura digital para esta proposta.',
        });
        return;
      }

      toaster.create({
        type: 'error',
        title: 'Erro ao iniciar assinatura digital',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    canStartDigital,
    createSignatureMutation,
    isSignatureActionPending,
    isViewMode,
    signatureMethodQuery,
    signatureQuery,
  ]);

  const handleSyncSignature = useCallback(async () => {
    if (
      isViewMode ||
      !canSync ||
      isSignatureActionPending ||
      brokerGuard.blocked
    ) {
      return;
    }

    try {
      const updated = await syncSignatureMutation.mutateAsync();

      if (updated.status === 'completed') {
        void queryClient.invalidateQueries({
          queryKey: capitalGlobalProposalQueryKey(trimmedPolicyId),
        });
        toaster.create({
          type: 'success',
          title: 'Proposta transmitida',
          description:
            'A assinatura foi concluída e a proposta foi marcada como transmitida.',
        });
        return;
      }

      toaster.create({
        type: 'info',
        title: 'Status atualizado',
        description: 'O progresso da assinatura foi sincronizado.',
      });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao verificar assinatura',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    brokerGuard.blocked,
    canSync,
    isSignatureActionPending,
    isViewMode,
    queryClient,
    syncSignatureMutation,
    trimmedPolicyId,
  ]);

  const handleRetrySignature = useCallback(async () => {
    if (
      isViewMode ||
      !canRetry ||
      isSignatureActionPending ||
      brokerGuard.blocked
    ) {
      return;
    }

    try {
      await retrySignatureMutation.mutateAsync();
      toaster.create({
        type: 'success',
        title: 'Retentativa iniciada',
        description: 'A assinatura digital está sendo reprocessada.',
      });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao retentar assinatura',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }, [
    brokerGuard.blocked,
    canRetry,
    isSignatureActionPending,
    isViewMode,
    retrySignatureMutation,
  ]);

  const closeSignatureMethodConfirm = useCallback(() => {
    setPendingSignatureMethod(null);
  }, []);

  const openPhysicalSignatureConfirm = useCallback(() => {
    if (
      isViewMode ||
      isPhysicalSignatureBlocked ||
      generatePdfMutation.isPending ||
      choosePhysicalSignatureMethodMutation.isPending
    ) {
      return;
    }

    setPendingSignatureMethod('physical');
  }, [
    choosePhysicalSignatureMethodMutation.isPending,
    generatePdfMutation.isPending,
    isPhysicalSignatureBlocked,
    isViewMode,
  ]);

  const openDigitalSignatureConfirm = useCallback(() => {
    if (
      isViewMode ||
      !canStartDigital ||
      generatePdfMutation.isPending ||
      createSignatureMutation.isPending
    ) {
      return;
    }

    setPendingSignatureMethod('digital');
  }, [
    canStartDigital,
    createSignatureMutation.isPending,
    generatePdfMutation.isPending,
    isViewMode,
  ]);

  const confirmSignatureMethod = useCallback(async () => {
    const method = pendingSignatureMethod;
    if (!method) return;

    try {
      if (method === 'physical') {
        await handlePhysicalSignature();
      } else {
        await handleDigitalSignature();
      }
    } finally {
      setPendingSignatureMethod(null);
    }
  }, [handleDigitalSignature, handlePhysicalSignature, pendingSignatureMethod]);

  const isSignatureMethodConfirmLoading =
    pendingSignatureMethod === 'physical'
      ? choosePhysicalSignatureMethodMutation.isPending ||
        generatePdfMutation.isPending
      : pendingSignatureMethod === 'digital'
        ? createSignatureMutation.isPending
        : false;

  return {
    premiumData,
    calculationData,
    isLoading,
    signature,
    lockedSignatureMethod,
    handleGeneratePdf,
    openPhysicalSignatureConfirm,
    openDigitalSignatureConfirm,
    closeSignatureMethodConfirm,
    confirmSignatureMethod,
    isSignatureMethodConfirmOpen: pendingSignatureMethod !== null,
    isSignatureMethodConfirmLoading,
    handleSyncSignature,
    handleRetrySignature,
    isGeneratePdfPending:
      generatePdfMutation.isPending || isDownloadingSignedPdf,
    canGeneratePdfInViewMode,
    isTransmitted,
    shouldHideSignatureActions,
    isPhysicalSignaturePending:
      pendingSignatureMethod === 'physical' &&
      (choosePhysicalSignatureMethodMutation.isPending ||
        generatePdfMutation.isPending),
    isCreateSignaturePending: createSignatureMutation.isPending,
    isSyncSignaturePending: syncSignatureMutation.isPending,
    isRetrySignaturePending: retrySignatureMutation.isPending,
    isPhysicalSignatureBlocked,
    physicalSignatureBlockedReason,
    digitalSignatureBlockedReason,
    canStartDigital,
    canSync,
    canRetry,
    isSignatureActionsBlocked: brokerGuard.blocked,
    signatureActionsBlockedReason: brokerGuard.reason,
    isGeneratePdfBlocked,
    generatePdfBlockedReason: isGeneratePdfBlocked
      ? brokerGuard.reason
      : undefined,
    canGoToUploadStep,
    onGoToUploadStep: onNext,
  };
}
